import React, { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, UserRound, Wand2 } from "lucide-react";
import Button from "../components/ui/Button";
import { Alert } from "../components/ui/Feedback";
import DownloadMenu from "../components/logo/DownloadMenu";
import { logoApi } from "../api/logoApi";
import { TRANSPARENCY_CHECKER_STYLE } from "./LogoGeneratorPage";
import { slugForFilename } from "../lib/logoExport";

let messageId = 0;
const nextId = () => `m${(messageId += 1)}`;

/**
 * One side shows the logo as it stands now; the other is a chat where each
 * instruction is sent to Gemini's image model along with the current image
 * and comes back as the next version. The server is stateless about which
 * image is "current" — this page is the source of truth, which is what
 * makes it possible to keep every version in the chat history for free.
 */
export default function LogoEditPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialImage = location.state?.image;
  const initialMimeType = location.state?.mimeType;
  const initialPrompt = location.state?.prompt;

  const [currentImage, setCurrentImage] = useState(initialImage);
  const [currentMimeType, setCurrentMimeType] = useState(
    initialMimeType || "image/png",
  );
  const [messages, setMessages] = useState(() =>
    initialImage
      ? [
          {
            id: nextId(),
            role: "assistant",
            text: "This is your logo. Tell me what you'd like to change — colors, shapes, mood, anything — and I'll update it here.",
          },
        ]
      : [],
  );
  const [instruction, setInstruction] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const scrollAnchor = useRef(null);
  useEffect(() => {
    scrollAnchor.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  if (!initialImage) {
    return <Navigate to="/logo-generator" replace />;
  }

  async function handleSend(event) {
    event.preventDefault();
    const text = instruction.trim();
    if (!text || sending) return;

    setMessages((m) => [...m, { id: nextId(), role: "user", text }]);
    setInstruction("");
    setSending(true);
    setError(null);

    try {
      const result = await logoApi.edit({
        imageDataUrl: currentImage,
        instruction: text,
      });
      setCurrentImage(result.dataUrl);
      setCurrentMimeType(result.mimeType || "image/png");
      setMessages((m) => [
        ...m,
        {
          id: nextId(),
          role: "assistant",
          text: "Updated.",
          image: result.dataUrl,
        },
      ]);
    } catch (err) {
      setError(err.message);
      setMessages((m) => [
        ...m,
        {
          id: nextId(),
          role: "assistant",
          text: `Couldn't apply that: ${err.message}`,
          isError: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend(event);
    }
  }

  return (
    <div className="mx-auto flex h-screen max-w-[1300px] flex-col px-4 py-4 sm:px-6 lg:px-8">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate("/logo-generator")}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-600 hover:text-ink-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
          Back to concepts
        </button>
        {initialPrompt && (
          <p className="truncate text-xs text-ink-500" title={initialPrompt}>
            Editing: <span className="text-ink-700">{initialPrompt}</span>
          </p>
        )}
      </div>

      <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-2">
        {/* Left — the logo as it stands now. */}
        <div className="flex min-h-0 flex-col rounded-soft border border-ink-200 bg-white shadow-raised">
          <div
            className="flex flex-1 items-center justify-center rounded-t-soft p-8"
            style={TRANSPARENCY_CHECKER_STYLE}
          >
            <img
              src={currentImage}
              alt="Current logo"
              className="max-h-full max-w-full object-contain drop-shadow-sm"
            />
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-ink-100 px-4 py-3">
            <p className="text-xs text-ink-500">
              {messages.filter((m) => m.role === "user").length} edit
              {messages.filter((m) => m.role === "user").length === 1
                ? ""
                : "s"}{" "}
              applied
            </p>
            <DownloadMenu
              dataUrl={currentImage}
              mimeType={currentMimeType}
              filenameBase={`${slugForFilename(initialPrompt, "logo")}-edited`}
            />
          </div>
        </div>

        {/* Right — chat. */}
        <div className="flex min-h-0 flex-col rounded-soft border border-ink-200 bg-white shadow-raised">
          <div className="flex items-center gap-2 border-b border-ink-100 px-4 py-3">
            <Sparkles
              className="h-4 w-4 text-[#8f5eeb]-500"
              aria-hidden="true"
            />
            <h2 className="font-display text-base font-semibold text-ink-900">
              Edit with Gemini
            </h2>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`flex max-w-[85%] items-start gap-2 ${
                    m.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                      m.role === "user"
                        ? "bg-ink-900 text-white"
                        : "bg-[#8f5eeb]-100 text-[#8f5eeb]-600"
                    }`}
                  >
                    {m.role === "user" ? (
                      <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                  </span>
                  <div
                    className={`rounded-soft px-3.5 py-2.5 text-sm ${
                      m.role === "user"
                        ? "bg-ink-900 text-white"
                        : m.isError
                          ? "bg-opposed-soft text-opposed"
                          : "bg-paper text-ink-800"
                    }`}
                  >
                    {m.image && (
                      <div
                        className="mb-2 h-16 w-16 overflow-hidden rounded border border-ink-200"
                        style={TRANSPARENCY_CHECKER_STYLE}
                      >
                        <img
                          src={m.image}
                          alt="Updated logo"
                          className="h-full w-full object-contain"
                        />
                      </div>
                    )}
                    <p>{m.text}</p>
                  </div>
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2 rounded-soft bg-paper px-3.5 py-2.5 text-sm text-ink-500">
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin"
                    aria-hidden="true"
                  />
                  Applying your edit…
                </div>
              </div>
            )}

            <div ref={scrollAnchor} />
          </div>

          {error && (
            <div className="px-4">
              <Alert tone="error">{error}</Alert>
            </div>
          )}

          <form
            onSubmit={handleSend}
            className="flex items-end gap-2 border-t border-ink-100 p-3"
          >
            <textarea
              rows={1}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Make the icon smaller and use a deep green instead"
              disabled={sending}
              className="control max-h-28 flex-1 resize-none"
            />
            <Button
              type="submit"
              variant="seal"
              disabled={!instruction.trim() || sending}
              loading={sending}
            >
              Send
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
